import { ColorChange } from "../../logo-list/hooks/useLogoActionsHandler";
import { Color } from "../../../../core/models/entities/LogoOptions";
import { ResolvedColorChange } from "../componenets/MutationProgress";

/**
 * Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: CSS scale for the canvas
 * image while the size fields differ from the SAVED size, so the logo grows/
 * shrinks with the typing before "Change size" is pressed. Uniform scale
 * (LogoResize keeps the aspect ratio); when a method change clipped one
 * axis the smaller ratio wins so the image never overshoots either field.
 * Clamped to [0.1, maxScale] where maxScale is what still fits the canvas
 * (1 = never grow beyond the box when it already fills the container).
 * Returns exactly 1 when nothing is typed or the saved size is unknown -
 * i.e. no transform at all in the common case - and when the form belongs to
 * ANOTHER logo (see formBelongsToLogo).
 */
export function getPreviewScale(
  form: { id?: number; widthMm: number; heightMm: number },
  saved: { id?: number; widthMm?: number; heightMm?: number },
  maxScale = 1
): number {
  if (!formBelongsToLogo(form, saved)) return 1;
  const savedW = saved.widthMm ?? 0;
  const savedH = saved.heightMm ?? 0;
  if (!(savedW > 0) || !(savedH > 0)) return 1;
  if (!(form.widthMm > 0) || !(form.heightMm > 0)) return 1;
  const ratio = Math.min(form.widthMm / savedW, form.heightMm / savedH);
  if (!Number.isFinite(ratio) || Math.abs(ratio - 1) < 1e-6) return 1;
  const upper = maxScale > 0 ? Math.max(1, maxScale) : 1;
  return Math.min(upper, Math.max(0.1, ratio));
}

/**
 * The editor form is view-model state initialised once per mount, and the
 * design editor can switch `logoToEdit` from its side panel while the tool
 * stays mounted (pre-existing: the form then still shows the previous logo).
 * Ruler and preview scale must never follow a form that belongs to ANOTHER
 * logo: false only when both ids are known and differ. An id-less side (the
 * standalone editor's empty form before an upload) cannot be attributed to a
 * different logo and is treated as belonging.
 */
export function formBelongsToLogo(
  form: { id?: number },
  logo: { id?: number }
): boolean {
  return form.id === undefined || logo.id === undefined || form.id === logo.id;
}

/**
 * Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: the mm the ruler shows -
 * the typed size while the form belongs to the open logo and the field is a
 * positive number, else the saved size (exactly what the ruler showed before
 * the flag).
 */
export function getRulerSizeMm(
  form: { id?: number; widthMm: number; heightMm: number },
  logo: { id?: number; widthMm?: number; heightMm?: number }
): { widthMm: number; heightMm: number } {
  const follow = formBelongsToLogo(form, logo);
  return {
    widthMm: follow && form.widthMm > 0 ? form.widthMm : (logo.widthMm ?? 0),
    heightMm:
      follow && form.heightMm > 0 ? form.heightMm : (logo.heightMm ?? 0),
  };
}

/**
 * Turns the raw ColorChange list from LogoColorChanger ({from, to} ids, `to`
 * undefined for untouched colours) into swatch pairs for the progress
 * chips. Untouched entries and ids missing from the catalog are dropped.
 */
export function resolveColorChanges(
  changes: ColorChange[] | null | undefined,
  catalog: Color[]
): ResolvedColorChange[] {
  if (!changes || changes.length === 0) return [];
  const byId = new Map(catalog.map((c) => [c.id, c] as const));
  const resolved: ResolvedColorChange[] = [];
  for (const change of changes) {
    if (!change.to) continue;
    const from = byId.get(change.from);
    const to = byId.get(change.to);
    if (!from || !to) continue;
    resolved.push({ from, to });
  }
  return resolved;
}
